const json = async (res) => {
  const body = await res.json().catch(() => ({}));
  if (res.status === 401) {
    // The session expired mid-session; let the shell swap in the login screen.
    window.dispatchEvent(new Event('auth:expired'));
  }
  if (!res.ok) throw new Error(body.error ?? `Request failed (${res.status})`);
  return body;
};

export const getMe = (signal) => fetch('/api/auth/me', { signal }).then(json);

export const login = (username, password) =>
  fetch('/api/auth/login', {
    method: 'POST',
    headers: { 'content-type': 'application/json' },
    body: JSON.stringify({ username, password })
  }).then(async (res) => {
    const body = await res.json().catch(() => ({}));
    if (!res.ok) throw new Error(body.error ?? 'Sign in failed');
    return body;
  });

export const logout = () => fetch('/api/auth/logout', { method: 'POST' }).then(json);

export const getUsers = () => fetch('/api/users').then(json);

export const addUser = (username, password) =>
  fetch('/api/users', {
    method: 'POST',
    headers: { 'content-type': 'application/json' },
    body: JSON.stringify({ username, password })
  }).then(json);

export const removeUser = (username) =>
  fetch(`/api/users/${encodeURIComponent(username)}`, { method: 'DELETE' }).then(json);

export const setUserPassword = (username, password) =>
  fetch(`/api/users/${encodeURIComponent(username)}/password`, {
    method: 'PUT',
    headers: { 'content-type': 'application/json' },
    body: JSON.stringify({ password })
  }).then(json);

export const getUsage = (days = 30) => fetch(`/api/usage?days=${days}`).then(json);

export const getHealth = () => fetch('/api/health').then(json);
// Which sections this deployment can serve. Public, so it resolves before sign-in.
export const getFeatures = () => fetch('/api/features').then(json);
export const getDiagnostics = () => fetch('/api/diagnostics').then(json);
export const searchStocks = (q, signal) => fetch(`/api/search?q=${encodeURIComponent(q)}`, { signal }).then(json);
export const getRunStatus = (runId) => fetch(`/api/screen/${runId}`).then(json);

export const getStock = (symbol, years, force = false, signal) =>
  fetch(`/api/stock/${encodeURIComponent(symbol)}?years=${years}&force=${force}`, { signal }).then(json);

export const getCandles = (symbol, days = 750, strategy = null) =>
  fetch(
    `/api/stock/${encodeURIComponent(symbol)}/candles?days=${days}` +
      (strategy ? `&strategy=${encodeURIComponent(strategy)}` : '')
  ).then(json);

export const startScreen = (body) =>
  fetch('/api/screen', {
    method: 'POST',
    headers: { 'content-type': 'application/json' },
    body: JSON.stringify(body)
  }).then(json);

export const getFundsMeta = () => fetch('/api/funds/meta').then(json);

export const getSwingMeta = () => fetch('/api/swing/meta').then(json);

export const getSwingTrades = (mode, universe) =>
  fetch(`/api/swing/trades?mode=${encodeURIComponent(mode)}&universe=${encodeURIComponent(universe)}`).then(json);

export const startSwingScan = (body) =>
  fetch('/api/swing/scan', {
    method: 'POST',
    headers: { 'content-type': 'application/json' },
    body: JSON.stringify(body)
  }).then(json);

export const startFundScreen = (body) =>
  fetch('/api/funds/screen', {
    method: 'POST',
    headers: { 'content-type': 'application/json' },
    body: JSON.stringify(body)
  }).then(json);

const sleep = (ms) => new Promise((r) => setTimeout(r, ms));

/**
 * Runs a screen to completion.
 *
 * The event stream only drives the progress bar. Completion is decided by
 * polling, because an EventSource fires `onerror` on any transient drop and a
 * long run would otherwise be abandoned while the server was still working.
 */
export async function runScreen(body, onProgress) {
  const { runId } = await startScreen(body);
  return followRun('/api/screen', runId, onProgress);
}

/** Same contract as runScreen, against the mutual fund endpoints. */
export async function runFundScreen(body, onProgress) {
  const { runId } = await startFundScreen(body);
  return followRun('/api/funds/screen', runId, onProgress);
}

/** Same contract again, against the swing endpoints. */
export async function runSwingScan(body, onProgress) {
  const { runId } = await startSwingScan(body);
  return followRun('/api/swing/scan', runId, onProgress);
}

async function followRun(base, runId, onProgress) {
  const stream = attachProgressStream(base, runId, onProgress);
  try {
    return await pollUntilDone(base, runId, onProgress);
  } finally {
    stream.close();
  }
}

function attachProgressStream(base, runId, onProgress) {
  let source = null;
  let closed = false;
  let retries = 0;

  const connect = () => {
    if (closed) return;
    source = new EventSource(`${base}/${runId}/events`);

    source.onmessage = (ev) => {
      let event;
      try {
        event = JSON.parse(ev.data);
      } catch {
        return;
      }
      retries = 0;
      if (event.type === 'progress') onProgress(event);
      if (event.type === 'close') {
        closed = true;
        source.close();
      }
    };

    source.onerror = () => {
      source.close();
      if (closed || retries >= 5) return;
      retries += 1;
      setTimeout(connect, 500 * retries);
    };
  };

  connect();
  return {
    close: () => {
      closed = true;
      source?.close();
    }
  };
}

async function pollUntilDone(base, runId, onProgress) {
  let consecutiveFailures = 0;

  for (;;) {
    await sleep(1200);
    let status;
    try {
      status = await fetch(`${base}/${runId}`).then(json);
      consecutiveFailures = 0;
    } catch (err) {
      consecutiveFailures += 1;
      if (consecutiveFailures >= 10) throw new Error(`Lost contact with the local server: ${err.message}`);
      continue;
    }

    if (status.state === 'done') return status.result;
    if (status.state === 'error') throw new Error(status.error);
    if (status.state === 'unknown') throw new Error('The server forgot this run. Start it again.');
    if (status.progress) onProgress(status.progress);
  }
}

/* --------------------------- my investments --------------------------- */

export const getPositions = (signal) => fetch('/api/positions', { signal }).then(json);

export const addPosition = (position) =>
  fetch('/api/positions', {
    method: 'POST',
    headers: { 'content-type': 'application/json' },
    body: JSON.stringify(position)
  }).then(json);

export const updatePosition = (id, position) =>
  fetch(`/api/positions/${encodeURIComponent(id)}`, {
    method: 'PATCH',
    headers: { 'content-type': 'application/json' },
    body: JSON.stringify(position)
  }).then(json);

export const closePosition = (id, exit) =>
  fetch(`/api/positions/${encodeURIComponent(id)}/close`, {
    method: 'POST',
    headers: { 'content-type': 'application/json' },
    body: JSON.stringify(exit)
  }).then(json);

export const removePosition = (id) =>
  fetch(`/api/positions/${encodeURIComponent(id)}`, { method: 'DELETE' }).then(json);

export const ignoreExitAlert = (id) =>
  fetch(`/api/positions/${encodeURIComponent(id)}/exit-alert/ignore`, { method: 'POST' }).then(json);
